import { ReactNode } from "react";
import { cn } from "../cn";

export type ButtonSize = "sm" | "md" | "lg";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "destructive";

interface ButtonProps {
  children: ReactNode;
  size?: ButtonSize;
  variant?: ButtonVariant;
  /** Icon before the text. Suppressed while `loading`, which takes its place. */
  startIcon?: ReactNode;
  /** Icon after the text. */
  endIcon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: "button" | "submit" | "reset";
  loading?: boolean;
  /** Required when the button's content is an icon with no text (WCAG 4.1.2). */
  "aria-label"?: string;
  fullWidth?: boolean;
}

/**
 * Every size clears the 44px touch target from the design standards, including
 * `sm` — a control does not get to be inaccessible because it is secondary.
 *
 * The label is wrapped in a span of its own rather than left as a bare text
 * node between the two conditional icon slots. A browser translating the page
 * (Chrome on Android does it unasked) replaces text nodes with its own
 * wrappers; React still holds the original, so the next `loading` flip tries
 * to insert the spinner before a node the button no longer owns and the render
 * dies with `NotFoundError: insertBefore` — Sentry ALCHELY-7, which came in
 * through this component. An element React created survives translation, so
 * the sibling reference stays valid. Same fix as #225 made in the meeting
 * pages, applied where every button gets it.
 */
const SIZE: Record<ButtonSize, string> = {
  sm: "min-h-11 px-3.5 py-2 text-sm",
  md: "min-h-11 px-4 py-2.5 text-sm",
  lg: "min-h-12 px-5 py-3 text-base",
};

const VARIANT: Record<ButtonVariant, string> = {
  // `accent` fill with `accent-contrast` ink — the pairing the contrast audit
  // asserts at 5.64:1 in both themes.
  primary: "bg-accent text-accent-contrast hover:bg-accent-hover",
  secondary: "bg-accent-tint text-accent-on-tint hover:bg-accent-tint/70",
  outline:
    "bg-surface-raised text-content-primary border border-border-strong hover:bg-surface-sunken",
  ghost: "bg-transparent text-content-secondary hover:bg-surface-sunken hover:text-content-primary",
  destructive:
    "bg-status-critical-fill text-content-inverse hover:bg-status-critical-fill/90",
};

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  startIcon,
  endIcon,
  onClick,
  className,
  disabled = false,
  type = "button",
  loading = false,
  fullWidth = false,
  "aria-label": ariaLabel,
}) => {
  const isInert = disabled || loading;

  return (
    <button
      type={type}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-semibold",
        "transition-colors duration-[--duration-fast] ease-[--ease-standard]",
        // Press feedback only. The previous `hover:scale-[1.02]` grew the
        // control under the cursor, which moves the target mid-gesture.
        "active:scale-[0.98]",
        SIZE[size],
        VARIANT[variant],
        fullWidth && "w-full",
        // A greyed-out control, not a pale tint of the accent: the old
        // `disabled:bg-brand-300` put white text at 2.30:1 and left disabled
        // buttons unreadable.
        isInert &&
          "cursor-not-allowed border-transparent bg-surface-sunken text-content-tertiary hover:bg-surface-sunken",
        className
      )}
      onClick={onClick}
      disabled={isInert}
    >
      {loading ? (
        <svg
          aria-hidden="true"
          className="size-4 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        startIcon && <span className="flex items-center">{startIcon}</span>
      )}
      <span>{children}</span>
      {!loading && endIcon && <span className="flex items-center">{endIcon}</span>}
    </button>
  );
};

export default Button;
